import { useQuery } from "@tanstack/react-query";
import { adminApi } from "@/api/admin.api";
import { Card, CardContent } from "@/components/ui/card";
import { PRODUCT_NAME } from "./admin-nav";
import { errorMessage, formatMoney, tierName } from "./first-round";
import { useAdminProduct } from "./hooks";
import { PageHeader } from "./ui/PageHeader";

export function Dashboard() {
  const { product } = useAdminProduct();
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-overview", product],
    queryFn: () => adminApi.getOverview(product),
  });

  const tiers = Object.entries((data?.tierDistribution ?? {}) as Record<string, number>).filter(
    ([, count]) => count > 0
  );

  return (
    <div className="max-w-5xl">
      <PageHeader
        title="Dashboard"
        description={`Subscriptions for ${PRODUCT_NAME[product]}. The user count covers both products.`}
      />
      {isLoading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : error || !data ? (
        <p className="text-sm text-red-600">Couldn't load the overview: {errorMessage(error)}</p>
      ) : (
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-3">
            <MetricCard label="Users (both products)" value={data.totalUsers} />
            <MetricCard label="Active subscriptions" value={data.activeUsers} />
            <MetricCard
              label="Estimated MRR"
              value={formatMoney(data.totalRevenue)}
              hint="Monthly list price of each active subscription. One-off passes aren't monthly."
            />
          </div>
          <Card>
            <CardContent className="pt-6">
              <div className="mb-3 text-sm font-medium text-slate-900">Active subscriptions by plan</div>
              {tiers.length === 0 ? (
                <p className="text-sm text-slate-500">No active subscriptions.</p>
              ) : (
                <dl className="grid gap-x-8 gap-y-2 sm:grid-cols-3">
                  {tiers.map(([tier, count]) => (
                    <div key={tier} className="flex justify-between border-b border-slate-100 pb-1 text-sm">
                      <dt className="text-slate-600">{tierName(product, tier)}</dt>
                      <dd className="font-medium tabular-nums">{count}</dd>
                    </div>
                  ))}
                </dl>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

function MetricCard({ label, value, hint }: { label: string; value: string | number; hint?: string }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="text-sm text-slate-500">{label}</div>
        <div className="mt-1 text-2xl font-semibold tabular-nums">{value}</div>
        {hint && <div className="mt-2 text-xs text-slate-500">{hint}</div>}
      </CardContent>
    </Card>
  );
}
